<template>
  <div class="pagination-container d-flex align-center justify-space-between py-4 px-6">
    <div class="d-flex align-center">
      <span class="text-body-2 text-medium-emphasis me-2">{{ $t('common.pagination.perPage') }}:</span>
      <v-select
        :model-value="itemsPerPage"
        :items="itemsPerPageOptions"
        variant="outlined"
        density="compact"
        bg-color="surface"
        hide-details
        class="items-per-page-select"
        @update:model-value="updateItemsPerPage"
      ></v-select>
    </div>
    
    <div class="d-flex align-center">
      <div class="pagination-info me-6">
        {{ $t('common.pagination.showing') }} {{ startItem }}-{{ endItem }} 
        {{ $t('common.pagination.of') }} {{ totalItems }}
      </div>
      
      <div class="pagination-controls">
        <v-btn
          icon="mdi-chevron-double-left"
          variant="text"
          :disabled="currentPage === 1"
          @click="changePage(1)"
          class="pagination-btn"
          density="comfortable"
          :ripple="false"
        ></v-btn>
        <v-btn
          icon="mdi-chevron-left"
          variant="text"
          :disabled="currentPage === 1"
          @click="changePage(currentPage - 1)"
          class="pagination-btn"
          density="comfortable"
          :ripple="false"
        ></v-btn>
      
        <div class="page-numbers mx-2">
          <template v-for="page in displayedPages" :key="page">
            <v-btn
              v-if="page !== '...'"
              :variant="page === currentPage ? 'elevated' : 'text'"
              :color="page === currentPage ? 'primary' : ''"
              size="small"
              class="mx-1 page-number-btn"
              @click="changePage(page)"
              :ripple="false"
            >
              {{ page }}
            </v-btn>
            <span v-else class="mx-1 text-medium-emphasis dots">...</span>
          </template>
        </div>
      
        <v-btn
          icon="mdi-chevron-right"
          variant="text"
          :disabled="currentPage === totalPages"
          @click="changePage(currentPage + 1)"
          class="pagination-btn"
          density="comfortable"
          :ripple="false"
        ></v-btn>
        <v-btn
          icon="mdi-chevron-double-right"
          variant="text"
          :disabled="currentPage === totalPages"
          @click="changePage(totalPages)"
          class="pagination-btn"
          density="comfortable"
          :ripple="false"
        ></v-btn>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

const props = defineProps({
  currentPage: {
    type: Number,
    required: true
  },
  totalItems: {
    type: Number,
    required: true
  },
  itemsPerPage: {
    type: Number,
    required: true
  },
  itemsPerPageOptions: {
    type: Array,
    default: () => [10, 25, 50, 100]
  }
})

const emit = defineEmits(['update:currentPage', 'update:itemsPerPage'])

const totalPages = computed(() => {
  return Math.ceil(props.totalItems / props.itemsPerPage) || 1
})

const startItem = computed(() => {
  return props.totalItems === 0 ? 0 : (props.currentPage - 1) * props.itemsPerPage + 1
})

const endItem = computed(() => {
  return Math.min(props.currentPage * props.itemsPerPage, props.totalItems)
})

// Calculate which page numbers to display
const displayedPages = computed(() => {
  const pages = []
  const maxVisiblePages = 5 // Maximum number of visible page numbers
  
  if (totalPages.value <= maxVisiblePages) {
    // If total pages is less than or equal to max visible pages,
    // show all pages
    for (let i = 1; i <= totalPages.value; i++) {
      pages.push(i)
    }
  } else {
    // Otherwise show some pages with ellipsis
    const leftBound = Math.max(1, props.currentPage - 2)
    const rightBound = Math.min(totalPages.value, props.currentPage + 2)
    
    // Always show first page
    pages.push(1)
    
    // Add ellipsis if current page is far from start
    if (leftBound > 2) {
      pages.push('...')
    }
    
    // Add pages around current page
    for (let i = leftBound; i <= rightBound; i++) {
      if (i !== 1 && i !== totalPages.value) {
        pages.push(i)
      }
    }
    
    // Add ellipsis if current page is far from end
    if (rightBound < totalPages.value - 1) {
      pages.push('...')
    }
    
    // Always show last page
    pages.push(totalPages.value)
  }
  
  return pages
})

const changePage = (newPage) => {
  if (newPage >= 1 && newPage <= totalPages.value) {
    emit('update:currentPage', newPage)
  }
}

const updateItemsPerPage = (value) => {
  emit('update:itemsPerPage', value)
  // Reset to first page when changing items per page
  emit('update:currentPage', 1)
}
</script>

<style scoped>
.pagination-container {
  border-top: 1px solid rgba(var(--v-theme-on-surface), 0.06);
  background-color: rgba(var(--v-theme-surface-variant), 0.02);
}

.pagination-btn {
  min-width: 36px;
  width: 36px;
  height: 36px;
  transition: all 0.2s ease;
}

.pagination-btn:not(:disabled):hover {
  background-color: rgba(var(--v-theme-primary), 0.05);
}

.pagination-info {
  font-size: 0.875rem;
  color: rgba(var(--v-theme-on-surface), 0.7);
  white-space: nowrap;
}

.items-per-page-select {
  width: 140px;
  transition: all 0.2s ease;
}

.page-number-btn {
  min-width: 32px;
  width: 32px;
  height: 32px;
  font-size: 0.875rem;
  transition: all 0.2s ease;
  font-weight: 500;
}

.page-numbers {
  display: flex;
  align-items: center;
}

.dots {
  opacity: 0.7;
  font-weight: bold;
  letter-spacing: 1px;
}

.pagination-controls {
  display: flex;
  align-items: center;
  background-color: rgba(var(--v-theme-surface), 0.8);
  border-radius: 8px;
  padding: 4px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

:deep(.v-field__input) {
  padding-top: 6px !important;
  padding-bottom: 6px !important;
}

:deep(.v-field) {
  border-radius: 6px;
}
</style>